// src/QRmessaging/QRViewer.js

import React, { useState } from 'react';
import './QRclient.css';
import { QRCodeSVG } from 'qrcode.react';
import { useNavigate } from 'react-router-dom';

const QRViewer = ({ language, message, user, onPasswordSubmit, SERVER_ROOT }) => {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const navigate = useNavigate();
  
  // 비밀번호 입력 폼 제출 처리
  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    onPasswordSubmit(message.id, password);
  };

  // QR 코드 표시 함수  
  const handleShowQRCode = () => {
    setShowQRModal(true);
  };
  
  // 비밀번호 보호 메시지 처리
  if (message.requiresPassword) {
    return (
      <div className="qr-viewer qr-password-form">
        <h2>{language === 'KR' ? '비밀번호로 보호된 메시지' : 'Password Protected Message'}</h2>
        <p>{language === 'KR' ? '이 메시지를 보려면 비밀번호를 입력하세요.' : 'Enter the password to view this message.'}</p>
        
        <form onSubmit={handlePasswordSubmit}>
          <div className="qr-form-group">
            <label htmlFor="password">
              {language === 'KR' ? '비밀번호' : 'Password'}:
            </label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          
          <button
            type="submit"
            className="qr-btn"
            disabled={loading}
          >
            {loading 
              ? (language === 'KR' ? '확인 중...' : 'Verifying...') 
              : (language === 'KR' ? '확인' : 'Submit')}
          </button>
        </form>
      </div>
    );
  }
  
  // 메시지 유형에 따른 렌더링
  const renderMessageContent = () => {
    switch (message.message_type) {
      case 'url':
        return (
          <div className="qr-url-content">
            <p>{language === 'KR' ? '다음 URL로 이동합니다:' : 'Navigate to this URL:'}</p>
            <a 
              href={message.message} 
              target="_blank" 
              rel="noopener noreferrer"
              className="qr-url-link"
            >
              {message.message}
            </a>
            <button
              onClick={() => window.open(message.message, '_blank')}
              className="qr-btn"
            >
              {language === 'KR' ? '링크 열기' : 'Open Link'}
            </button>
          </div>
        );
        
      case 'vcard':
        try {
          const vcardData = JSON.parse(message.message);
          return (
            <div className="qr-vcard-content">
              <div className="qr-vcard">
                <h3>{vcardData.fullName}</h3>
                {vcardData.title && <p className="qr-vcard-title">{vcardData.title}</p>}
                {vcardData.company && <p className="qr-vcard-company">{vcardData.company}</p>}
                
                <div className="qr-vcard-details">
                  {vcardData.email && (
                    <div className="qr-vcard-detail">
                      <span className="qr-vcard-label">
                        {language === 'KR' ? '이메일' : 'Email'}:
                      </span>
                      <a href={`mailto:${vcardData.email}`}>{vcardData.email}</a>
                    </div>
                  )}
                  
                  {vcardData.phone && (
                    <div className="qr-vcard-detail">
                      <span className="qr-vcard-label">
                        {language === 'KR' ? '전화' : 'Phone'}:
                      </span>
                      <a href={`tel:${vcardData.phone}`}>{vcardData.phone}</a>
                    </div>
                  )}
                  
                  {vcardData.address && (
                    <div className="qr-vcard-detail">
                      <span className="qr-vcard-label">
                        {language === 'KR' ? '주소' : 'Address'}:
                      </span>
                      <p>{vcardData.address}</p>
                    </div>
                  )}
                </div>
                
                <button
                  onClick={() => {
                    const vcf = generateVCF(vcardData);
                    const blob = new Blob([vcf], { type: 'text/vcard' });
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `${vcardData.fullName}.vcf`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}
                  className="qr-btn"
                >
                  {language === 'KR' ? '연락처 저장' : 'Save Contact'}
                </button>
              </div>
            </div>
          );
        } catch (e) {
          console.error('명함 데이터 파싱 오류:', e);
          return (
            <div className="qr-error">
              <p>{language === 'KR' ? '명함 데이터 형식이 잘못되었습니다.' : 'Invalid vCard format.'}</p>
            </div>
          );
        }
        
      case 'text':
      default:
        return (
          <div className="qr-text-content">
            <pre>{message.message}</pre>
          </div>
        );
    }
  };
  
  // VCF 파일 생성 헬퍼 함수
  const generateVCF = (data) => {
    return `BEGIN:VCARD
    VERSION:3.0
    FN:${data.fullName || ''}
    ${data.title ? `TITLE:${data.title}` : ''}
    ${data.company ? `ORG:${data.company}` : ''}
    ${data.email ? `EMAIL:${data.email}` : ''}
    ${data.phone ? `TEL:${data.phone}` : ''}
    ${data.address ? `ADR:;;${data.address};;;` : ''}
    END:VCARD`;
      };
    
  // QR 코드 모달 컴포넌트 추가 및 렌더링
  const QRCodeModal = ({ show, onClose }) => {
    if (!show) return null;
    
    const messageUrl = window.location.href;
    
    return (
      <div className="qr-modal">
        <div className="qr-modal-content">
          <div className="qr-modal-header">
            <h3>{language === 'KR' ? 'QR 코드' : 'QR Code'}</h3>
            <button className="qr-modal-close" onClick={onClose}>&times;</button>
          </div>
          <div className="qr-modal-body">
            <div className="qr-code-container" id="qrCodeContainer">
              <QRCodeSVG
                value={messageUrl}
                size={250}
                level="H"
                includeMargin={true}
              />
            </div>
            <p className="qr-modal-url">{messageUrl}</p>
          </div>
          <div className="qr-modal-footer">
            <button
              className="qr-btn"
              onClick={() => {
                try {
                  // SVG 요소 선택 및 데이터 추출
                  const svgElement = document.querySelector('#qrCodeContainer svg');
                  const svgData = new XMLSerializer().serializeToString(svgElement);
                  
                  // SVG 데이터를 Base64로 인코딩
                  const svgBase64 = btoa(unescape(encodeURIComponent(svgData)));
                  
                  // 이미지 생성 및 로드
                  const img = new Image();
                  img.onload = function() {
                    // Canvas 생성 및 이미지 그리기
                    const canvas = document.createElement('canvas');
                    canvas.width = img.width;
                    canvas.height = img.height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0);
                    
                    // Canvas에서 PNG 데이터 URL 생성
                    const pngUrl = canvas.toDataURL('image/png');
                    
                    // 다운로드 링크 생성 및 클릭
                    const downloadLink = document.createElement('a');
                    downloadLink.href = pngUrl;
                    downloadLink.download = `qrcode-message-${message.id}.png`;
                    document.body.appendChild(downloadLink);
                    downloadLink.click();
                    document.body.removeChild(downloadLink);
                  };
                  img.src = 'data:image/svg+xml;base64,' + svgBase64;
                } catch (error) {
                  console.error('QR 코드 저장 오류:', error);
                  alert(language === 'KR' 
                    ? 'QR 코드 저장 중 오류가 발생했습니다.' 
                    : 'Error saving QR code.');
                }
              }}
            >
              {language === 'KR' ? 'QR 코드 저장' : 'Save QR Code'}
            </button>
            <button
              className="qr-btn qr-btn-secondary"
              onClick={() => {
                try {
                  // SVG 요소 선택 및 데이터 추출
                  const svgElement = document.querySelector('#qrCodeContainer svg');
                  const svgData = new XMLSerializer().serializeToString(svgElement);
                  
                  // SVG 데이터를 Base64로 인코딩
                  const svgBase64 = btoa(unescape(encodeURIComponent(svgData)));
                  
                  // 이미지 생성 및 로드
                  const img = new Image();
                  img.onload = function() {
                    // Canvas 생성 및 이미지 그리기
                    const canvas = document.createElement('canvas');
                    canvas.width = img.width;
                    canvas.height = img.height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0);
                    
                    // Canvas에서 Blob 생성
                    canvas.toBlob(blob => {
                      // 새로운 ClipboardItem 생성
                      const clipboardItem = new ClipboardItem({ 'image/png': blob });
                      
                      // 클립보드에 복사
                      navigator.clipboard.write([clipboardItem])
                        .then(() => {
                          alert(language === 'KR' 
                            ? 'QR 코드가 클립보드에 복사되었습니다!' 
                            : 'QR code copied to clipboard!');
                        })
                        .catch(err => {
                          console.error('클립보드 복사 오류:', err);
                          alert(language === 'KR' 
                            ? '클립보드에 복사할 수 없습니다. 브라우저가 이 기능을 지원하지 않을 수 있습니다.' 
                            : 'Cannot copy to clipboard. Your browser may not support this feature.');
                        });
                    });
                  };
                  img.src = 'data:image/svg+xml;base64,' + svgBase64;
                } catch (error) {
                  console.error('QR 코드 복사 오류:', error);
                  alert(language === 'KR' 
                    ? 'QR 코드 복사 중 오류가 발생했습니다. 브라우저가 이 기능을 지원하지 않을 수 있습니다.' 
                    : 'Error copying QR code. Your browser may not support this feature.');
                }
              }}
            >
              {language === 'KR' ? 'QR 코드 복사' : 'Copy QR Code'}
            </button>
          </div>
        </div>
      </div>
    );
  };
  
  // 메시지 액션 부분 수정
  // QRViewer.js의 메시지 액션 버튼 부분 수정
  return (
    <div className="qr-viewer">
      <div className="qr-message-header">
        <h2>{language === 'KR' ? 'QR 코드 메시지' : 'QR Code Message'}</h2>
        
        {/* 메시지 정보 표시 */}
        {message.sender_email && (
          <p className="qr-message-sender">
            {language === 'KR' ? '보낸 사람' : 'From'}: {message.sender_email}
          </p>
        )}
        
        {message.created_at && (
          <p className="qr-message-date">
            {language === 'KR' ? '생성 일시' : 'Created'}: {new Date(message.created_at).toLocaleString()}
          </p>
        )}
      </div>
      
      <div className="qr-message-body">
        {/* 템플릿에 따른 메시지 내용 렌더링 */}
        {renderMessageContent()}
      </div>
      
      <div className="qr-message-actions">
        {/* 응답 또는 공유 버튼 등 */}
        {message.sender_email && message.sender_email !== 'public' && (
          <button
            onClick={() => {
              navigate('/qr');
            }}
            className="qr-btn"
          >
            {language === 'KR' ? '보낸 사람에게 응답' : 'Reply to Sender'}
          </button>
        )}
        
        <button
          onClick={handleShowQRCode}
          className="qr-btn"
        >
          {language === 'KR' ? 'QR 코드 보기' : 'View QR Code'}
        </button>
      </div>
  
      {/* QR 코드 모달 */}
      <QRCodeModal
        show={showQRModal}
        onClose={() => setShowQRModal(false)}
      />
    </div>
  );
};

export default QRViewer;